/**
 * SpawnManager - Handles player spawn and respawn logic
 * 
 * Responsibilities:
 * - Finding spawn positions via terrain raycast
 * - Tracking spawn readiness (terrain loaded at spawn point)
 * - Finding respawn positions after falling
 * - Debug visualization for spawn detection
 * 
 * Uses the TerrainProbe interface (voxel-field queries) to decouple from VoxelIntegration.
 */

import * as THREE from 'three';
import { 
  PLAYER_HEIGHT, 
  SPAWN_HEIGHT_OFFSET, 
  SPAWN_FALLBACK_HEIGHT,
  SPAWN_RAYCAST_HEIGHT,
} from '@worldify/shared';
import type { TerrainProbe } from './TerrainProbe';
import { columnProbeForExtent } from './columnProbe';

/**
 * Configuration for SpawnManager
 */
export interface SpawnConfig {
  /** Whether to show spawn debug visualization */
  showDebug?: boolean;
}

const DEFAULT_CONFIG: Required<SpawnConfig> = {
  showDebug: true,
};

/**
 * SpawnManager handles all spawn/respawn position finding.
 */
export class SpawnManager {
  private config: Required<SpawnConfig>;
  private scene: THREE.Scene;
  private terrainProvider: TerrainProbe | null = null;

  // Spawn state
  private cachedSpawnPosition: THREE.Vector3 | null = null;
  private spawnFound = false;
  // XZ the ground scan targets (default origin; set to a biome cell centre for spawn-biome selection).
  private spawnTargetX = 0;
  private spawnTargetZ = 0;
  
  // Debug visualization
  private debugObjects: THREE.Object3D[] = [];

  constructor(scene: THREE.Scene, config: SpawnConfig = {}) {
    this.scene = scene;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * Set the terrain provider for raycasting.
   */
  setTerrainProvider(provider: TerrainProbe): void {
    this.terrainProvider = provider;
  }

  /** Set the XZ the ground scan targets (a chosen biome's cell centre). Resets spawn detection so the
   *  next terrain update re-probes at the new target. Pass (0,0) for the default origin spawn. */
  setSpawnTarget(x: number, z: number): void {
    if (x === this.spawnTargetX && z === this.spawnTargetZ) return;
    this.spawnTargetX = x;
    this.spawnTargetZ = z;
    this.spawnFound = false;   // clearing this re-probes on the next update
    this.cachedSpawnPosition = null;
  }

  /**
   * Update spawn detection.
   * Call during spectator mode to find spawn point when terrain loads.
   */
  update(): void {
    if (!this.terrainProvider) return;

    // Probe until ground is found. The voxel probe answers from chunk DATA, which is
    // resident well before a mesh exists, so spawn readiness no longer waits on meshing —
    // and re-probing is cheap enough to just do each update instead of guessing at
    // readiness from a mesh count.
    if (!this.spawnFound) {
      const ground = this.findGroundNear(this.spawnTargetX, this.spawnTargetZ);
      if (ground) {
        this.cachedSpawnPosition = new THREE.Vector3(ground.x, this.calculateSpawnY(ground.height), ground.z);
        this.spawnFound = true;
      }
    }
  }

  /**
   * Find ground at a target column.
   *
   * The old mesh raycast needed a 9-point fan here, because a ray aimed exactly at a
   * 4-chunk corner seam could slip between triangles. A voxel column probe is cell-exact
   * and has no seams to slip through, so one probe at the target is enough.
   */
  private findGroundNear(x: number, z: number): { x: number; z: number; height: number } | null {
    const height = this.raycastTerrainHeight(x, z);
    if (height !== null) return { x, z, height };
    return null;
  }

  /**
   * Check if spawn point has been found via raycast.
   */
  isSpawnReady(): boolean {
    return this.spawnFound;
  }

  /**
   * Get the cached spawn position (from spectator mode detection).
   * Falls back to getSpawnPosition if not cached.
   */
  getCachedSpawnPosition(): THREE.Vector3 {
    if (this.cachedSpawnPosition) {
      return this.cachedSpawnPosition.clone();
    }
    return this.getSpawnPosition(0, 0);
  }

  /**
   * Get spawn position by raycasting down to find terrain.
   * Falls back to high position if no terrain found.
   * 
   * @param x World X coordinate
   * @param z World Z coordinate
   * @returns Spawn position
   */
  getSpawnPosition(x: number = 0, z: number = 0): THREE.Vector3 {
    const ground = this.findGroundNear(x, z);

    if (ground) {
      return new THREE.Vector3(ground.x, this.calculateSpawnY(ground.height), ground.z);
    }

    // Fallback: spawn HIGH to let gravity bring player down. Above the loaded terrain when we know how
    // tall it is — a flat 50 m is below the surface in a kilometre-scale world, which would drop the
    // player inside a mountain instead of onto it.
    console.warn('[SpawnManager] No terrain found, using fallback height');
    const extent = this.terrainProvider?.loadedYExtent() ?? null;
    const top = extent ? columnProbeForExtent(extent.minY, extent.maxY).terrainTop : null;
    return new THREE.Vector3(x, Math.max(SPAWN_FALLBACK_HEIGHT, top ?? SPAWN_FALLBACK_HEIGHT), z);
  }

  /**
   * Ground-resolved spawn position for a column, or null when that column has no ground yet.
   *
   * The honest counterpart to `getSpawnPosition`, which always answers (falling back to a height
   * guess). Callers that hold a position from a NON-PHYSICS source — the explore marker, which
   * picks against render meshes including coarse far-LOD rings and so can be tens of metres off
   * the lod-0 surface at zoom — use this to re-resolve Y against the voxel field, and keep their
   * own value when it returns null (the column isn't resident; the freeze contract holds the
   * player until it is).
   */
  resolveGroundSpawn(x: number, z: number): THREE.Vector3 | null {
    const ground = this.findGroundNear(x, z);
    if (!ground) return null;
    return new THREE.Vector3(ground.x, this.calculateSpawnY(ground.height), ground.z);
  }

  /**
   * Find a safe respawn position for a player who fell too long.
   *
   * Priority:
   * 1. Last grounded position (if available)
   * 2. Ground at current XZ
   * 3. null (terrain not ready — hold position, ask again when it streams)
   *
   * THERE IS DELIBERATELY NO ORIGIN FALLBACK. It used to be option 3, and it was the funnel that
   * turned every failure into "back at the original spawn": a player spawned across the map with
   * no grounded history whose column hadn't streamed got teleported to the origin surface — and
   * once grounded there, that became their anchor forever. Returning null instead matches the
   * contract PlayerManager.handleRespawnNeeded already states: when we cannot place the player,
   * do not guess. The fall check re-arms and asks again once chunks arrive.
   *
   * @param currentPos Current player position
   * @param lastGroundedPos Last position where player was grounded
   * @returns Respawn position, or null if terrain not ready
   */
  findRespawnPosition(
    currentPos: THREE.Vector3,
    lastGroundedPos: THREE.Vector3 | null
  ): THREE.Vector3 | null {
    // Option 1: Use last grounded position
    if (lastGroundedPos) {
      return lastGroundedPos.clone();
    }

    // Option 2: Ground near current XZ
    return this.resolveGroundSpawn(currentPos.x, currentPos.z);
  }

  /**
   * Calculate spawn Y from terrain height (DRY helper).
   */
  private calculateSpawnY(terrainHeight: number): number {
    return terrainHeight + PLAYER_HEIGHT + SPAWN_HEIGHT_OFFSET;
  }

  /**
   * Raycast down from a high point to find terrain surface.
   * 
   * @param x World X coordinate
   * @param z World Z coordinate
   * @returns Y coordinate of terrain surface, or null if not found
   */
  private raycastTerrainHeight(x: number, z: number): number | null {
    if (!this.terrainProvider) return null;

    // SOLID ONLY. The old probe raycast every layer including liquid and foliage, so it would
    // happily report the surface of a lake or the top of a leaf canopy as ground and spawn the
    // player there.
    //
    // SIZED FROM THE LOADED TERRAIN, not from the constant. SPAWN_RAYCAST_HEIGHT is the scan's
    // ORIGIN, so on kilometre-scale terrain a fixed 200 m start sits *inside* the mountain and the
    // downward scan reports no ground on a peak that is plainly there; the length matters
    // independently, because a fixed `origin * 2` stops short of an ocean floor hundreds of metres
    // down, and deep oceans arrive together with tall mountains.
    const extent = this.terrainProvider.loadedYExtent();
    const probe = extent
      ? columnProbeForExtent(extent.minY, extent.maxY)
      : { originY: SPAWN_RAYCAST_HEIGHT, far: SPAWN_RAYCAST_HEIGHT * 2, terrainTop: null };

    const ground = this.terrainProvider.probeDown(x, z, probe.originY, probe.far, true);
    if (!ground) return null;

    if (this.config.showDebug) {
      this.showDebugVisualization(
        new THREE.Vector3(x, probe.originY, z),
        new THREE.Vector3(x, ground.y, z)
      );
    }
    return ground.y;
  }

  /**
   * Show debug visualization for spawn raycast.
   */
  private showDebugVisualization(rayOrigin: THREE.Vector3, hitPoint: THREE.Vector3): void {
    this.clearDebugVisualization();
    
    // Line from ray origin to hit
    const lineMaterial = new THREE.LineBasicMaterial({ 
      color: 0x00ff00, 
      depthTest: false,
      transparent: true,
      opacity: 0.8
    });
    const lineGeometry = new THREE.BufferGeometry().setFromPoints([rayOrigin, hitPoint]);
    const line = new THREE.Line(lineGeometry, lineMaterial);
    line.renderOrder = 999;
    this.scene.add(line);
    this.debugObjects.push(line);
    
    // Hit marker (red sphere)
    const hitMarker = this.createMarker(hitPoint, 0.3, 0xff0000);
    this.scene.add(hitMarker);
    this.debugObjects.push(hitMarker);
    
    // Spawn position marker (cyan sphere)
    const spawnY = this.calculateSpawnY(hitPoint.y);
    const spawnMarker = this.createMarker(
      new THREE.Vector3(hitPoint.x, spawnY, hitPoint.z), 
      0.2, 
      0x00ffff
    );
    this.scene.add(spawnMarker);
    this.debugObjects.push(spawnMarker);
  }

  /**
   * Create a debug marker sphere.
   */
  private createMarker(position: THREE.Vector3, radius: number, color: number): THREE.Mesh {
    const geometry = new THREE.SphereGeometry(radius, 16, 16);
    const material = new THREE.MeshBasicMaterial({ 
      color,
      depthTest: false,
      transparent: true,
      opacity: 0.8
    });
    const marker = new THREE.Mesh(geometry, material);
    marker.position.copy(position);
    marker.renderOrder = 999;
    return marker;
  }

  /**
   * Clear spawn debug visualization.
   */
  clearDebugVisualization(): void {
    for (const obj of this.debugObjects) {
      this.scene.remove(obj);
      if (obj instanceof THREE.Mesh || obj instanceof THREE.Line) {
        obj.geometry.dispose();
        (obj.material as THREE.Material).dispose();
      }
    }
    this.debugObjects = [];
  }

  /**
   * Reset spawn state for reconnection.
   * Clears cached spawn position and forces re-detection.
   */
  reset(): void {
    this.cachedSpawnPosition = null;
    this.spawnFound = false;
    this.clearDebugVisualization();
  }

  /**
   * Dispose all resources.
   */
  dispose(): void {
    this.clearDebugVisualization();
    this.terrainProvider = null;
  }
}
